import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowBigUp, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getStudentToken } from '../authCookie';
import { apiErrorText } from '@/lib/apiError';
import { confusionColor, confusionLabel, CONFUSION_GRADIENT } from '@/components/ConfusionMeter';

interface PromptInfo {
  prompt_id: string;
  prompt_text: string;
  position: number;
  is_closed: boolean;
  my_level?: number | null;
}

interface QuestionInfo {
  question_id: string;
  question_text: string;
  votes: number;
}

interface ReviewStudentInterfaceProps {
  sessionId: string;
  classCode: string;
  expiresAt?: string | null;
  backendUrl?: string;
}

const POLL_MS = 5000;
const SEND_DEBOUNCE_MS = 350;

function authHeaders(): Record<string, string> {
  const t = getStudentToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** One prompt with the student's own understanding slider. Debounces sends the
 * same way the live confusion meter does. */
function PromptCard({
  prompt,
  backendUrl,
  onSaved,
}: {
  prompt: PromptInfo;
  backendUrl?: string;
  onSaved: (promptId: string, level: number) => void;
}) {
  const [local, setLocal] = useState<number>(prompt.my_level ?? 100);
  const [touched, setTouched] = useState<boolean>(prompt.my_level != null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (prompt.my_level != null) {
      setLocal(prompt.my_level);
      setTouched(true);
    }
  }, [prompt.my_level]);

  useEffect(() => () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
  }, []);

  const send = useCallback(
    async (level: number) => {
      try {
        await fetch(`${backendUrl}/api/prompts/${prompt.prompt_id}/understanding`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ level }),
        });
        onSaved(prompt.prompt_id, level);
      } catch {
        // Best-effort; the next poll reconciles.
      }
    },
    [backendUrl, prompt.prompt_id, onSaved]
  );

  const handleChange = (vals: number[]) => {
    const next = vals[0] ?? 0;
    setLocal(next);
    setTouched(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      send(next);
    }, SEND_DEBOUNCE_MS);
  };

  const color = confusionColor(local);

  return (
    <li className="rounded-2xl border bg-card p-5 shadow-soft">
      <div className="flex items-start justify-between gap-4">
        <p className="font-medium">{prompt.prompt_text}</p>
        <span
          className="shrink-0 rounded-full px-3 py-1 text-xs font-semibold text-white"
          style={{ backgroundColor: touched ? color : undefined }}
          data-untouched={!touched || undefined}
        >
          {touched ? confusionLabel(local) : 'Not set'}
        </span>
      </div>
      <div className="mt-5">
        <Slider
          value={[local]}
          min={0}
          max={100}
          step={1}
          disabled={prompt.is_closed}
          onValueChange={handleChange}
          aria-label={`How well you understand: ${prompt.prompt_text}`}
          className="[&_[data-orientation=horizontal]]:h-3 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-2 [&_[role=slider]]:border-white [&_[role=slider]]:shadow-card"
          trackStyle={{ backgroundImage: CONFUSION_GRADIENT }}
          thumbStyle={{ backgroundColor: color }}
        />
        <div className="mt-2.5 flex justify-between text-xs font-medium text-muted-foreground">
          <span>Lost</span>
          <span>Got it</span>
        </div>
      </div>
      {prompt.is_closed && (
        <p className="mt-3 text-xs text-muted-foreground">This topic is closed.</p>
      )}
    </li>
  );
}

export default function ReviewStudentInterface({
  sessionId,
  classCode,
  expiresAt,
  backendUrl,
}: ReviewStudentInterfaceProps) {
  const [prompts, setPrompts] = useState<PromptInfo[]>([]);
  const [questions, setQuestions] = useState<QuestionInfo[]>([]);
  const [myUpvotes, setMyUpvotes] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [pRes, qRes] = await Promise.all([
        fetch(`${backendUrl}/api/prompts/${sessionId}`, { headers: authHeaders() }),
        fetch(`${backendUrl}/api/questions/${sessionId}`, { headers: authHeaders() }),
      ]);
      if (pRes.ok) {
        const data = await pRes.json();
        setPrompts(data.prompts || []);
      }
      if (qRes.ok) {
        const data = await qRes.json();
        setQuestions(data.questions || []);
        setMyUpvotes(new Set<string>(data.my_upvotes || []));
      }
    } catch {
      // Transient; the next poll retries.
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl, sessionId]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  // Keep the student's own slider value across polls without clobbering it.
  const handleSavedLevel = useCallback((promptId: string, level: number) => {
    setPrompts((prev) =>
      prev.map((p) => (p.prompt_id === promptId ? { ...p, my_level: level } : p))
    );
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setIsSubmitting(true);
    setNotice(null);
    try {
      const res = await fetch(`${backendUrl}/api/questions/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: sessionId, question_text: text }),
      });
      const data = await res.json();
      if (!res.ok) {
        setNotice(apiErrorText(data?.detail, 'Could not submit your question.'));
        return;
      }
      setDraft('');
      setNotice('Question submitted!');
      refresh();
    } catch {
      setNotice('Unable to reach the server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleUpvote = async (questionId: string) => {
    const voted = myUpvotes.has(questionId);
    const endpoint = voted ? 'downvote' : 'upvote';
    // Optimistic update; the poll reconciles if the server disagrees.
    setMyUpvotes((prev) => {
      const next = new Set(prev);
      if (voted) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
    setQuestions((prev) =>
      prev.map((q) =>
        q.question_id === questionId ? { ...q, votes: q.votes + (voted ? -1 : 1) } : q
      )
    );
    try {
      await fetch(`${backendUrl}/api/questions/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: sessionId, question_id: questionId }),
      });
    } catch {
      refresh();
    }
  };

  const closesOn = expiresAt
    ? new Date(expiresAt).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const sortedQuestions = [...questions].sort((a, b) => b.votes - a.votes);

  return (
    <main className="container max-w-2xl py-8">
      <div className="flex items-center gap-2 rounded-xl border bg-muted/40 px-4 py-3 text-sm">
        <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-muted-foreground">
          Review session for <span className="font-semibold text-foreground">{classCode}</span>
          {closesOn ? ` · open until ${closesOn}` : ''}
        </span>
      </div>

      <section className="mt-8" aria-labelledby="prompts-heading">
        <h2 id="prompts-heading" className="text-lg font-semibold tracking-tight">
          Topics to review
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Set how well you understand each topic your professor posted.
        </p>
        {isLoading ? (
          <Skeleton className="mt-4 h-40 w-full rounded-2xl" />
        ) : prompts.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            No topics yet. Your professor may add some during the review window.
          </p>
        ) : (
          <ul className="mt-4 space-y-4">
            {prompts.map((p) => (
              <PromptCard key={p.prompt_id} prompt={p} backendUrl={backendUrl} onSaved={handleSavedLevel} />
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10" aria-labelledby="questions-heading">
        <h2 id="questions-heading" className="text-lg font-semibold tracking-tight">
          Ask a question
        </h2>
        <form onSubmit={handleSubmit} className="mt-3 space-y-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What would you like the professor to cover?"
            rows={3}
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground" aria-live="polite">{notice}</span>
            <Button type="submit" disabled={isSubmitting || !draft.trim()}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </form>

        <ul className="mt-6 space-y-3">
          {sortedQuestions.map((q) => {
            const voted = myUpvotes.has(q.question_id);
            return (
              <li key={q.question_id} className="flex items-start gap-3 rounded-xl border bg-card p-4 shadow-soft">
                <button
                  type="button"
                  onClick={() => toggleUpvote(q.question_id)}
                  aria-pressed={voted}
                  aria-label={voted ? 'Remove upvote' : 'Upvote'}
                  className={cn(
                    'flex shrink-0 flex-col items-center rounded-lg border px-2.5 py-1.5 text-sm font-semibold transition-colors',
                    voted ? 'border-transparent bg-brand text-brand-foreground' : 'bg-background hover:bg-muted'
                  )}
                >
                  <ArrowBigUp className="h-4 w-4" aria-hidden />
                  {q.votes}
                </button>
                <p className="min-w-0 flex-1 text-sm">{q.question_text}</p>
              </li>
            );
          })}
        </ul>
      </section>
    </main>
  );
}
